/**
 * Followed — the user's shortlist: the scenarios they're tracking (pinned on top)
 * and the hospitals they're keeping an eye on, plus "for each followed scenario,
 * which of our hospitals is the cheapest option."
 *
 * This is the surface the future per-hospital "year in review" report builds on:
 * the same followed scenarios + hospitals + per-scenario coverage prices.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { Building2, ArrowRight, TrendingDown, Star, MapPin, Activity, Info } from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { loadStaticData } from "@/lib/offlineData";
import { hospitalStorage, type FollowedHospital } from "@/lib/hospitalStorage";
import { scenarioStorage } from "@/lib/scenarioStorage";
import FollowHospitalButton from "@/components/FollowHospitalButton";
import FollowScenarioButton from "@/components/FollowScenarioButton";

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${Math.round(n).toLocaleString()}`;

export default function FollowedHospitals() {
  const brand = useBrand();
  const isAhp = brand.key === "ahp";
  const { isDark } = useTheme();
  const dark = isAhp && isDark;

  // ── Followed hospitals (live) ──────────────────────────────────────────────
  const [followed, setFollowed] = useState<FollowedHospital[]>([]);
  useEffect(() => {
    const sync = () => setFollowed(hospitalStorage.getFollowed());
    sync();
    window.addEventListener("ahp-followed-changed", sync);
    return () => window.removeEventListener("ahp-followed-changed", sync);
  }, []);

  // ── Followed scenarios (live, from bookmarks) ──────────────────────────────
  const [followedScenarioIds, setFollowedScenarioIds] = useState<string[]>([]);
  useEffect(() => {
    const sync = () => setFollowedScenarioIds(scenarioStorage.getBookmarks());
    sync();
    window.addEventListener("ahp-scenario-bookmarks-changed", sync);
    return () => window.removeEventListener("ahp-scenario-bookmarks-changed", sync);
  }, []);

  // Full scenario index (for resolving followed IDs + popular fallback).
  const { data: indexRows = [] } = useQuery({
    queryKey: ["followed-scenario-index"],
    queryFn: async () => {
      const d = await loadStaticData<{ data: any[] }>("scenarios_index.json");
      return d?.data || [];
    },
    staleTime: 1000 * 60 * 60,
  });

  const indexById = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of indexRows) m.set(r.id, r);
    return m;
  }, [indexRows]);

  const followedScenarios = useMemo(
    () => followedScenarioIds.map((id) => indexById.get(id)).filter(Boolean),
    [followedScenarioIds, indexById]
  );

  const followedHospIds = useMemo(() => followed.map((f) => f.id).join(","), [followed]);
  const followedScenIdsKey = useMemo(() => followedScenarioIds.join(","), [followedScenarioIds]);

  // For each scenario, look up each followed hospital's price — using the
  // per-hospital reverse index (hospital_scenarios/<id>.json), which lists every
  // scenario a hospital actually has qualified pricing for. (The old approach
  // checked a fixed set of "popular" scenarios against the per-scenario files,
  // so a hospital like UT Health Athens — which covers thousands of scenarios but
  // few of the arbitrary popular ones — showed "no data" everywhere.)
  const { data: matrix = [], isLoading: matrixLoading } = useQuery({
    queryKey: ["followed-matrix-v2", followedHospIds, followedScenIdsKey],
    enabled: followed.length > 0,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      // 1. Load each followed hospital's covered scenarios → Map<scenarioId, price>.
      const hospMaps = new Map<string, Map<string, number>>();
      await Promise.all(
        followed.map(async (f) => {
          const m = new Map<string, number>();
          try {
            const d = await loadStaticData<{ s: Array<{ s: string; hp: number }> }>(`hospital_scenarios/${f.id}.json`);
            for (const r of d?.s || []) if (r.hp > 0) m.set(r.s, r.hp);
          } catch { /* hospital file missing — leave empty */ }
          hospMaps.set(f.id, m);
        })
      );

      // 2. Which scenarios to compare across? The user's followed scenarios when
      //    they have any; otherwise the scenarios the MOST of their hospitals
      //    cover (so the comparison is meaningful), capped to a readable set.
      let scenarioIds: string[];
      if (followedScenarioIds.length > 0) {
        scenarioIds = followedScenarioIds;
      } else {
        const counts = new Map<string, number>();
        for (const m of hospMaps.values()) for (const sid of m.keys()) counts.set(sid, (counts.get(sid) || 0) + 1);
        scenarioIds = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15).map((e) => e[0]);
      }

      // 3. Build the per-scenario comparison rows.
      const rows = scenarioIds.map((sid) => {
        const meta = indexById.get(sid) || {};
        const prices = followed.map((f) => ({ id: f.id, name: f.name, price: hospMaps.get(f.id)?.get(sid) ?? null }));
        const priced = prices.filter((p) => p.price != null) as Array<{ id: string; name: string; price: number }>;
        priced.sort((a, b) => a.price - b.price);
        return {
          scenario: { id: sid, name: meta.name || "Scenario", slug: meta.slug || "" },
          prices,
          cheapest: priced[0] || null,
        };
      });
      // Scenarios with data first; drop fully-empty ones when not explicitly followed.
      rows.sort((a, b) => (b.cheapest ? 1 : 0) - (a.cheapest ? 1 : 0));
      return followedScenarioIds.length > 0 ? rows : rows.filter((r) => r.cheapest);
    },
  });

  // Per-hospital cheapest scenario across the compared set.
  const perHospital = useMemo(() => {
    const map = new Map<string, { lo?: { name: string; price: number } }>();
    for (const row of matrix) {
      for (const p of row.prices) {
        if (p.price == null) continue;
        const e = map.get(p.id) || {};
        if (!e.lo || p.price < e.lo.price) e.lo = { name: row.scenario.name, price: p.price };
        map.set(p.id, e);
      }
    }
    return map;
  }, [matrix]);

  const C = {
    bg: dark ? "#0a1628" : "#FAF9F6",
    card: dark ? "#0d1e35" : "#ffffff",
    border: dark ? "rgba(197,165,90,0.2)" : "rgba(27,27,27,0.1)",
    text: dark ? "#f0ebe3" : "#1B1B1B",
    muted: dark ? "rgba(240,235,227,0.6)" : "rgba(27,27,27,0.62)",
    red: "#B22234",
    navy: dark ? "#7da6d4" : "#002868",
    green: "#2b5e49",
    gold: "#C5A55A",
    serif: "'Instrument Serif', Georgia, serif",
  };

  const nothingFollowed = followed.length === 0 && followedScenarios.length === 0;

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: C.bg, color: C.text }}>
      <Helmet><title>Followed — Your Shortlist | American Hospital Prices</title></Helmet>

      <section className="max-w-[800px] mx-auto px-5 pt-6 pb-3">
        <div className="flex items-center gap-2 mb-1" style={{ color: C.gold }}>
          <Star className="w-4 h-4" fill={C.gold} />
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>Your Shortlist</span>
        </div>
        <h1 style={{ fontFamily: C.serif, fontSize: "clamp(28px,6vw,40px)", fontWeight: 400, lineHeight: 1.1 }}>Followed</h1>
        <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.5, marginTop: 6 }}>
          The scenarios and hospitals you’re tracking — and which of your hospitals is the best option for each scenario your family might face.
        </p>
        {/* Tooltip helper */}
        <div className="flex items-start gap-2 mt-3 rounded-lg p-2.5" style={{ backgroundColor: dark ? "rgba(197,165,90,0.08)" : "rgba(0,40,104,0.05)", border: `1px solid ${C.border}` }}>
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.navy }} />
          <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.45 }}>
            Tap the <Star className="inline w-3 h-3 align-[-1px]" style={{ color: C.gold }} fill={C.gold} /> star on any scenario card or hospital to add it here. Everything is saved on this device — no account needed.
          </p>
        </div>
      </section>

      {nothingFollowed ? (
        <section className="max-w-[800px] mx-auto px-5 pt-6">
          <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: C.card, border: `1px solid ${C.border}` }}>
            <Star className="w-10 h-10 mx-auto mb-3" style={{ color: C.gold }} />
            <h2 style={{ fontFamily: C.serif, fontSize: 22, marginBottom: 6 }}>Nothing followed yet</h2>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.5, maxWidth: 460, margin: "0 auto 16px" }}>
              Tap the ★ on any scenario card to track it here, and the ★ on any hospital (in a scenario’s nearby list or on a hospital profile) to add it to your shortlist. Then compare your hospitals across the scenarios that matter to you.
            </p>
            <Link to="/scenarios" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold" style={{ backgroundColor: C.red, color: "#fff", fontSize: 14 }}>
              Browse scenarios <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      ) : (
        <>
          {/* ── Followed scenarios (PINNED TOP) ──────────────────────────────── */}
          <section className="max-w-[800px] mx-auto px-5 pt-2">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4" style={{ color: C.red }} />
              <h2 style={{ fontFamily: C.serif, fontSize: 18 }}>Followed scenarios ({followedScenarios.length})</h2>
            </div>
            {followedScenarios.length === 0 ? (
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: C.card, border: `1px dashed ${C.border}`, color: C.muted, fontSize: 13 }}>
                No followed scenarios yet — tap the ★ on a scenario card to pin it here.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {followedScenarios.map((s) => (
                  <div key={s.id} className="rounded-xl p-3" style={{ backgroundColor: C.card, border: `1px solid ${C.border}` }}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <Link to={`/scenarios/${s.id}/${s.slug}`} className="block" style={{ fontFamily: C.serif, fontSize: 16, color: C.text, lineHeight: 1.25 }}>{s.name}</Link>
                        <div className="mt-1 flex items-center gap-2" style={{ fontSize: 12 }}>
                          <span className="font-mono font-bold" style={{ color: C.red }}>{fmt(s.estimatedCost)}</span>
                          <span style={{ color: C.muted }}>national avg</span>
                        </div>
                      </div>
                      <FollowScenarioButton scenarioId={s.id} dark={dark} size={18} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Followed hospitals ───────────────────────────────────────────── */}
          <section className="max-w-[800px] mx-auto px-5 pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4" style={{ color: C.navy }} />
              <h2 style={{ fontFamily: C.serif, fontSize: 18 }}>Followed hospitals ({followed.length})</h2>
            </div>
            {followed.length === 0 ? (
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: C.card, border: `1px dashed ${C.border}`, color: C.muted, fontSize: 13 }}>
                No followed hospitals yet — tap the ★ on a hospital in a scenario’s nearby list or on its profile.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {followed.map((h) => {
                  const ph = perHospital.get(h.id);
                  return (
                    <div key={h.id} className="rounded-xl p-3" style={{ backgroundColor: C.card, border: `1px solid ${C.border}` }}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <Link to={`/hospital/${h.id}`} className="block truncate" style={{ fontFamily: C.serif, fontSize: 16, color: C.text }}>{h.name}</Link>
                          <div className="flex items-center gap-1 mt-0.5" style={{ color: C.muted, fontSize: 12 }}>
                            <MapPin className="w-3 h-3" /> {h.city}, {h.state}
                          </div>
                        </div>
                        <FollowHospitalButton hospital={{ id: h.id, name: h.name, city: h.city, state: h.state }} dark={dark} size={18} />
                      </div>
                      {ph?.lo && (
                        <div className="flex items-center gap-3 mt-2 pt-2 text-[11px]" style={{ borderTop: `1px solid ${C.border}` }}>
                          <span className="flex items-center gap-1" style={{ color: C.green }}>
                            <TrendingDown className="w-3 h-3" /> {fmt(ph.lo.price)} <span style={{ color: C.muted }}>cheapest ({ph.lo.name})</span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── Best option per scenario ─────────────────────────────────────── */}
          {followed.length > 0 && (
            <section className="max-w-[800px] mx-auto px-5 pt-6">
              <h2 className="mb-1" style={{ fontFamily: C.serif, fontSize: 18 }}>Best of your hospitals, by scenario</h2>
              <p className="mb-3" style={{ color: C.muted, fontSize: 13 }}>
                {followedScenarios.length > 0
                  ? "For each scenario you follow, the lowest-priced of your followed hospitals."
                  : "Follow some scenarios above to compare your hospitals on the ones you care about. Showing common scenarios for now."}
              </p>
              {matrixLoading && <div style={{ color: C.muted, fontSize: 14 }}>Comparing your hospitals…</div>}
              <div className="space-y-2">
                {matrix.map((row) => (
                  <div key={row.scenario.id} className="rounded-xl p-3" style={{ backgroundColor: C.card, border: `1px solid ${C.border}` }}>
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/scenarios/${row.scenario.id}/${row.scenario.slug}`} className="truncate" style={{ fontFamily: C.serif, fontSize: 15, color: C.text }}>
                        {row.scenario.name}
                      </Link>
                      {row.cheapest ? (
                        <span className="flex-shrink-0 font-mono font-bold" style={{ color: C.green, fontSize: 14 }}>{fmt(row.cheapest.price)}</span>
                      ) : (
                        <span className="flex-shrink-0" style={{ color: C.muted, fontSize: 12 }}>no data</span>
                      )}
                    </div>
                    {row.cheapest ? (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" style={{ fontSize: 11 }}>
                        <span className="px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: `${C.green}15`, color: C.green }}>Best: {row.cheapest.name}</span>
                        {row.prices.filter((p) => p.price != null && p.id !== row.cheapest!.id).sort((a, b) => (a.price! - b.price!)).map((p) => (
                          <span key={p.id} style={{ color: C.muted }}>{p.name.split(" ").slice(0, 2).join(" ")} {fmt(p.price)}</span>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1" style={{ color: C.muted, fontSize: 11 }}>None of your hospitals have qualified pricing for this scenario yet.</div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
